import { Directive, ElementRef, OnDestroy, effect, inject } from '@angular/core';
import { SkipperShowService } from '../engine/skipper-show.service';

/**
 * Fades app chrome out — invisible and untappable — for as long as Skipper's
 * show runs, and back when it stops. Port of Flutter's `SkipperShow.hide`.
 *
 * Fades rather than unmounts: removing a control would destroy its state, and
 * the promise of the show is that stopping it puts the user back exactly where
 * they were. Uses the Web Animations API so the host's own CSS transitions are
 * left alone.
 */
@Directive({
  selector: '[appSkipperHideDuringShow]',
  standalone: true,
})
export class SkipperHideDuringShowDirective implements OnDestroy {
  private el = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private show = inject(SkipperShowService);
  private animation: Animation | null = null;

  constructor() {
    effect(() => this.apply(this.show.onStage()));
  }

  ngOnDestroy(): void {
    this.animation?.cancel();
  }

  private hidden = false;

  private apply(hidden: boolean): void {
    if (hidden === this.hidden) return;
    this.hidden = hidden;
    if (typeof this.el.animate !== 'function') {
      this.el.style.visibility = hidden ? 'hidden' : '';
      return;
    }
    const from = this.animation ? getComputedStyle(this.el).opacity : hidden ? '1' : '0';
    this.animation?.cancel();
    // The house lights going down: short and eased out.
    this.animation = this.el.animate([{ opacity: from }, { opacity: hidden ? 0 : 1 }], {
      duration: 220,
      easing: 'ease-out',
      fill: hidden ? 'forwards' : 'none',
    });
    this.el.style.pointerEvents = hidden ? 'none' : '';
    this.el.toggleAttribute('inert', hidden);
  }
}
